/*
 *  Collection Template
 *    Contains all the scripts used for the collection template.
 *
 *  Version:
 *    1.0.0 - 2023/06/05
 */
import { Section } from '../tools/section';
import { Swiper } from 'swiper';
import { Navigation, Pagination, Scrollbar } from 'swiper/modules';
import { CLASS_HIDDEN, CLASS_ACTIVE, CLASS_OVERFLOW } from '../tools/constants/classes';
import { MediaThumbnail } from '../objects/media-thumbnail';
import { ProductQuickView } from '../components/product-quick-view/ProductQuickView';
import AOS from 'aos';

// Selectors
const SELECTOR_CONTAINER    =   '[data-featured-products-carousel]';
const SELECTOR_SWIPER_CONTAINER = '[data-swiper-container]';
const SELECTOR_SWIPER_NEXT = '[data-swiper-next]';
const SELECTOR_SWIPER_PREV = '[data-swiper-prev]';
const SELECTOR_SWIPER_SCROLLBAR = '[data-swiper-scrollbar]';
const SELECTOR_CAROUSEL_TAB = '[data-carousel-tab]';
const SELECTOR_CAROUSEL_TABS = '[data-carousel-tab-item]'
const SELECTOR_CAROUSEL_ITEMS = '[data-carousel-slide-item]'
const SELECTOR_NO_SWIPING = '[data-swiper-no-swiping]';
const SELECTOR_MEDIA_THUMBNAIL = '[data-media-thumbnail]';

class FeaturedProductsCarousel extends Section  {
  private swiperInstances: Map<string, Swiper> = new Map();
  private activeTab: string | null = null;
  private collectionTab: HTMLElement | null = null;;
  private collectionTabs: NodeListOf<HTMLElement> | null = null;
  private mediaThumbnailElements: NodeListOf<HTMLElement> | null = null;
  private productQuickView: ProductQuickView | null = null;

  private resetAOSClasses(panel: HTMLElement) {
    panel.classList.remove('aos-animate', 'aos-init');
    // Clear on panel itself
    (panel as HTMLElement).style.transitionDelay = '';
    (panel as HTMLElement).style.animationDelay = '';

    panel.querySelectorAll<HTMLElement>('[data-aos]').forEach((node) => {
      node.classList.remove('aos-animate', 'aos-init');
      // Clear inline delays that AOS may have set previously
      node.style.transitionDelay = '';
      node.style.animationDelay = '';
    });
  }

  queryChildren(container: HTMLElement): void {
    this.collectionTab = container.querySelector(SELECTOR_CAROUSEL_TAB);
    this.collectionTabs = container.querySelectorAll(SELECTOR_CAROUSEL_TABS);
    this.mediaThumbnailElements = container.querySelectorAll(SELECTOR_MEDIA_THUMBNAIL);
    this.renderActiveSlider(container);
  }

  attachEvents() {
    if (this.collectionTabs) {
      this.collectionTabs.forEach(tab => {
        tab.addEventListener('click', (e) => {
          const target = e.currentTarget as HTMLElement;
          const tabItem = target.getAttribute('data-carousel-tab-item');
          if (tabItem) {
            this.activateSlider(tabItem);
          }
        });
      });
    }

    AOS.init({ duration: 800, easing: 'ease-out', once: true });

    if (this.mediaThumbnailElements) {
      this.mediaThumbnailElements.forEach(container => {
        new MediaThumbnail(container);
      });
    }

    this.checkTabOverflow();
    window.addEventListener('resize', () => {
      this.checkTabOverflow();
    });

    this.productQuickView = new ProductQuickView();
  }

  private checkTabOverflow() {
    if (!this.collectionTab || !this.collectionTabs) return;
    const containerWidth = this.collectionTab.offsetWidth;
    // Set dynamic CSS custom property for tab width
    this.collectionTab.style.setProperty('--carousel-tab-width', containerWidth + 'px');
    let totalTabsWidth = 0;
    this.collectionTabs.forEach(tab => {
      totalTabsWidth += tab.offsetWidth;
    });
    if (totalTabsWidth > containerWidth) {
      this.collectionTab.classList.add(CLASS_OVERFLOW);
    } else {
      this.collectionTab.classList.remove(CLASS_OVERFLOW);
    }
  }

  detatchEvents(): void {}

  renderActiveSlider(container: HTMLElement) {
    const tabsContainer = this.collectionTab;
    
    // Check if tabs are enabled and exist in the DOM
    if (tabsContainer) {
      const firstTab = tabsContainer.querySelector('button');
      if (firstTab) {
        const tabItem = firstTab.getAttribute('data-carousel-tab-item');
        if (tabItem) {
          this.activateSlider(tabItem);
        }
      }
      this.checkTabOverflow();
    } else {
      // No tabs container - handle single carousel or fallback scenario
      // Find the first available carousel and activate it directly
      const firstCarousel = container.querySelector(SELECTOR_SWIPER_CONTAINER) as HTMLElement | null;
      if (firstCarousel) {
        // Initialize the carousel without tab switching logic
        this.initializeSwiper(firstCarousel);
        firstCarousel.classList.remove(CLASS_HIDDEN);
        this.applyAOSStagger(firstCarousel);
        
        // Refresh AOS for the visible carousel
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            AOS.refreshHard();
          });
        });
      }
    }
  }

  activateSlider(tabItem: string | null) {
    if (!tabItem || this.activeTab === tabItem) return;
    this.activeTab = tabItem;

    // Remove CLASS_ACTIVE from the previously active tab
    this.container.querySelectorAll(SELECTOR_CAROUSEL_TABS).forEach(tab => {
      tab.classList.remove(CLASS_ACTIVE);
    });

    // Add CLASS_ACTIVE to the currently active tab
    const activeTabElement = document.querySelector(`[data-carousel-tab-item="${tabItem}"]`);
    if (activeTabElement) {
      activeTabElement.classList.add(CLASS_ACTIVE);
    }

    this.container.querySelectorAll(SELECTOR_SWIPER_CONTAINER).forEach(container => {
      const containerTabItem = container.getAttribute('data-swiper-container');
      if (containerTabItem === tabItem) {
        if (!this.swiperInstances.has(tabItem)) {
          this.initializeSwiper(container);
        }
        // reset AOS so items can animate again
        this.resetAOSClasses(container);
        container.classList.remove(CLASS_HIDDEN);
        this.applyAOSStagger(container);
        // Double-rAF to ensure styles/attributes are committed before refresh
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            AOS.refreshHard();
          });
        });
      } else {
        container.classList.add(CLASS_HIDDEN);
        // prepare hidden panels to re-animate on next activation
        this.resetAOSClasses(container);
      }
    });
  }

  initializeSwiper(container: HTMLElement) {
    const swiper = new Swiper(container, {
      modules: [Scrollbar, Pagination, Navigation],
      slidesPerView: 'auto',
      noSwipingSelector: SELECTOR_NO_SWIPING,
      touchMoveStopPropagation: true,
      scrollbar: {
        el: container.querySelector(SELECTOR_SWIPER_SCROLLBAR),
        draggable: true,
      },
      navigation: {
        nextEl: container.querySelector(SELECTOR_SWIPER_NEXT),
        prevEl: container.querySelector(SELECTOR_SWIPER_PREV),
      },
      keyboard: true,
      grabCursor: true,
    });
    this.swiperInstances.set(container.getAttribute('data-swiper-container')!, swiper);
  }

  private applyAOSStagger(panel: HTMLElement) {
    const slides = panel.querySelectorAll<HTMLElement>(SELECTOR_CAROUSEL_ITEMS);
    slides.forEach((el, idx) => {
      const delay = idx * 160; // ms
      // Ensure required attributes exist
      el.dataset.aos = el.dataset.aos || 'fade-up';
      el.dataset.aosOnce = 'true';
      el.setAttribute('data-aos-delay', String(delay));
      // Force the delay even if AOS doesn't re-read attributes
      el.style.transitionDelay = `${delay}ms`;
      el.style.animationDelay = `${delay}ms`;
    });
  }
}

Section.register(SELECTOR_CONTAINER, FeaturedProductsCarousel);